import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  LinkIcon,
} from "@heroicons/react/24/solid";

import {
  faHeading,
  faBold,
  faItalic,
  faListUl,
  faLink,
  faCode,
  faQuoteRight,
  faImage,
  faTable,
  faGripLines,
} from "@fortawesome/free-solid-svg-icons";

const BloggingPage = () => {
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [viewingBlog, setViewingBlog] = useState(null); // State for viewing blog
  const [suggestions, setSuggestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  // Fetch tag suggestions from API
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (tagInput.trim() === "") {
        setSuggestions([]);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tags?search=${tagInput}`
        );
        setSuggestions(res.data);
      } catch (err) {
        console.error("Failed to fetch suggestions:", err);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300); // debounce
    return () => clearTimeout(delayDebounce);
  }, [tagInput]);

  const handleAddTag = (e) => {
    e.preventDefault();
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput("");
    setSuggestions([]);
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSelectSuggestion = (suggestion) => {
    if (!tags.includes(suggestion.name)) {
      setTags([...tags, suggestion.name]);
    }
    setTagInput("");
    setSuggestions([]);
  };

  // Filter blogs based on the search term
  const filteredBlogs = blogs.filter((blog) =>
    blog.heading.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    let isMounted = true; // flag to track component mount status

    const fetchComments = async (blogId) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/comments/${blogId}`
        );
        if (isMounted) {
          setComments((prev) => ({
            ...prev,
            [blogId]: response.data, // Store comments using blogId as key
          }));
        }
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    };

    if (viewingBlog) {
      fetchComments(viewingBlog._id); // Fetch comments for the blog being viewed
    }

    return () => {
      isMounted = false; // Cleanup flag on unmount
    };
  }, [viewingBlog]);

  // Fetch blogs on component mount
  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    const email = sessionStorage.getItem("email");
    const username = sessionStorage.getItem("username");

    if (!email || !username) {
      alert("User is not authenticated");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/posts/filter?email=${email}&username=${username}`
      );
      const data = await response.json();

      if (response.ok) {
        setBlogs(data);
      } else {
        alert("Error fetching blogs: " + data.message);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const handleSaveBlog = async () => {
    if (!heading.trim()) {
      alert("Please enter a blog heading");
      return;
    }

    const email = sessionStorage.getItem("email");
    const username = sessionStorage.getItem("username");

    if (!email || !username) {
      alert("User is not authenticated");
      return;
    }

    const newBlog = {
      heading,
      tags,
      content,
      email,
      username,
    };

    try {
      if (editingBlog) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/post/${editingBlog._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(newBlog),
          }
        );

        if (!response.ok) throw new Error("Failed to update the blog");

        const updatedBlog = await response.json();
        setBlogs(
          blogs.map((blog) =>
            blog._id === editingBlog._id ? updatedBlog : blog
          )
        );
        setEditingBlog(null);
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/post`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(newBlog),
          }
        );

        if (!response.ok) throw new Error("Failed to create the blog");

        const createdBlog = await response.json();
        setBlogs([...blogs, createdBlog]);
      }

      setHeading("");
      setTags("");
      setContent("");
      setPreview(false);

      // Refresh the page
      window.location.reload();
    } catch (error) {
      console.error("Error saving the blog:", error);
      alert("An error occurred while saving the blog.");
    }
  };

  const handleEditBlog = (blog) => {
    setHeading(blog.heading);
    setTags(blog.tags);
    setContent(blog.content);
    setEditingBlog(blog);
  };

  const handleDeleteBlog = async (blogId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this post?"
    );

    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/post/${blogId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) throw new Error("Failed to delete the blog");

      window.location.reload(); // <-- Refresh the page
    } catch (error) {
      console.error("Error deleting the blog:", error);
      alert("An error occurred while deleting the blog.");
    }
  };

  const handleCommentfetch = async (e) => {
    e.preventDefault();
    const user = {
      username: sessionStorage.getItem("username"),
      email: sessionStorage.getItem("email"),
    };

    if (!user.username || !user.email) {
      alert("You must be logged in to comment!");
      return;
    }

    const newComment = {
      text: commentInput,
      blogId: viewingBlog._id,
      username: user.username,
      email: user.email,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/comments`,
        newComment
      );

      if (response.status === 200) {
        // Update the comments state for the specific blog post
        setComments((prevComments) => ({
          ...prevComments,
          [viewingBlog._id]: [
            ...(prevComments[viewingBlog._id] || []),
            newComment,
          ],
        }));
        setCommentInput(""); // Reset comment input
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };
  return (
    <div className="min-h-screen  bg-black flex flex-col items-center p-6">
      {" "}
      <div
        className="w-full  bg-gray-800 shadow-md rounded-lg p-6 focus:outline-none border border-blue-600 focus:ring-2 focus:ring-purple-500"
        tabIndex="0"
      >
        <div className="flex flex-col items-center justify-center text-center my-2">
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
            Dashboard
          </h1>
          <p className="text-base font-semibold text-gray-400">
            Your content, your control...
          </p>
        </div>

        {/* Blog Creation Form */}
        <div className="mb-4">
          <label className="block text-gray-200 text-sm font-bold mb-2">
            Blog Heading
          </label>
          <input
            type="text"
            className="w-full p-3 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Enter your blog title here..."
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-200 text-sm font-bold mb-2">
            Add Markdown Syntax
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n# Heading 1\n")}
            >
              <FontAwesomeIcon icon={faHeading} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n## Heading 2\n")}
            >
              <FontAwesomeIcon icon={faHeading} />
              <sup className="text-xs">2</sup>
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n**Bold Text**\n")}
            >
              <FontAwesomeIcon icon={faBold} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n*Italic Text*\n")}
            >
              <FontAwesomeIcon icon={faItalic} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n- List Item\n")}
            >
              <FontAwesomeIcon icon={faListUl} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() =>
                setContent(content + "\n[Link](https://example.com)\n")
              }
            >
              <FontAwesomeIcon icon={faLink} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n`Inline Code`\n")}
            >
              <FontAwesomeIcon icon={faCode} />
            </button>

            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n> Blockquote\n")}
            >
              <FontAwesomeIcon icon={faQuoteRight} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() =>
                setContent(
                  content + "\n![Alt Text](https://example.com/image.png)\n"
                )
              }
            >
              <FontAwesomeIcon icon={faImage} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() =>
                setContent(
                  content +
                    "\n| Column 1 | Column 2 | Column 3 |\n|-----------|-----------|-----------|\n| Row 1 Col 1 | Row 1 Col 2 | Row 1 Col 3 |\n| Row 2 Col 1 | Row 2 Col 2 | Row 2 Col 3 |\n"
                )
              }
            >
              <FontAwesomeIcon icon={faTable} />
            </button>
            <button
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs sm:text-base"
              onClick={() => setContent(content + "\n---\n")}
            >
              <FontAwesomeIcon icon={faGripLines} />
            </button>
          </div>
        </div>

        {/* Tags Input */}
        <div className="mb-4">
          <label className="block text-gray-200 text-sm font-bold mb-2">
            Tags
          </label>
          <form
            onSubmit={handleAddTag}
            className="flex flex-wrap items-center gap-2 relative"
          >
            <input
              type="text"
              className="p-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Add a tag and press Enter"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
            />
            {suggestions.length > 0 && (
              <ul className="absolute top-12  bg-gray-800 text-white w-full border border-gray-600 rounded-md z-10 max-h-48 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <li
                    key={suggestion._id}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="p-2 hover:bg-purple-700 cursor-pointer"
                  >
                    {suggestion.name}
                  </li>
                ))}
              </ul>
            )}
            {tags.map((tag, index) => (
              <div
                key={index}
                className="flex items-center bg-purple-700 text-white px-3 py-1 rounded-full"
              >
                <span>{tag}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-2 text-sm hover:text-red-300 focus:outline-none"
                >
                  ✕
                </button>
              </div>
            ))}
          </form>
        </div>

        {/* Markdown Content Input */}
        <div className=" mt-5 mb-4">
          <label className="block text-gray-200 text-sm font-bold mb-2">
            Blog Content (Markdown)
          </label>
          <textarea
            className="w-full h-48 p-3 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Write your blog in Markdown format..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-center items-center space-x-4">
          <button
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-lg transform hover:scale-105 transition duration-200"
            onClick={() => setPreview(true)}
          >
            Preview
          </button>
          <button
            className="px-6 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-md hover:from-green-600 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-green-500 shadow-lg transform hover:scale-105 transition duration-200"
            onClick={handleSaveBlog}
          >
            {editingBlog ? "Update" : "Save"}
          </button>
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 flex justify-center items-start pt-12 bg-black bg-opacity-50">
          <div className="w-full max-w-4xl bg-gray-900 border border-gray-700 shadow-lg rounded-lg p-6 relative overflow-y-auto max-h-[90vh]">
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 bg-red-600 text-white p-2 rounded-md animate-bounce shadow-md hover:bg-red-700 transition duration-200 focus:outline-none"
              onClick={() => setPreview(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Inner Layout */}
            <div className="bg-white rounded-lg shadow-inner p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                {heading || "Untitled Blog"}
              </h2>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="text-sm text-purple-600 font-semibold"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="prose max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content || "Nothing to preview..."}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Blog List Section */}
      <div className="w-full max-w-full  shadow-md rounded-lg p-4 sm:p-6 border border-blue-600 mt-6 mx-auto">
        <div className="flex justify-center items-center mb-6">
          <h1 className="text-2xl font-bold text-center text-gray-400">
          Published Blogs
          </h1>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            className="w-full p-2 bg-gray-700 text-white rounded-md"
            placeholder="Search blogs by heading..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredBlogs.length === 0 ? (
          <p className="text-gray-500">No blogs found. Try another search!</p>
        ) : (
          <div className="bg-gray-900 rounded-lg p-4 h-96 overflow-y-auto">
            {/* Blog List Container with Scroll */}
            <div className="space-y-4">
              {filteredBlogs.map((blog) => (
                <div
                  key={blog._id}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-lg rounded-lg p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center"
                >
                  <div className="flex-1 mb-4 sm:mb-0">
                    <h3 className="text-lg sm:text-xl font-semibold text-white">
                      {blog.heading}
                    </h3>
                    <p className="text-sm text-gray-200">
                      Created: {new Date(blog.createdAt).toLocaleString()}
                    </p>
                    <p className="text-sm font-bold text-white">
                      ❤️ {blog.Total_reaction}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                    {/* View Button */}
                    <button
                      className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 transition ease-in-out duration-300 flex items-center space-x-2 w-full sm:w-auto"
                      onClick={() => setViewingBlog(blog)}
                    >
                      <EyeIcon className="w-5 h-5" /> {/* View Icon */}
                      <span>View</span>
                    </button>

                    {/* Edit Button */}
                    <button
                      className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 transition ease-in-out duration-300 flex items-center space-x-2 w-full sm:w-auto"
                      onClick={() => handleEditBlog(blog)}
                    >
                      <PencilSquareIcon className="w-5 h-5" /> {/* Edit Icon */}
                      <span>Edit</span>
                    </button>

                    {/* Delete Button */}
                    <button
                      className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 transition ease-in-out duration-300 flex items-center space-x-2 w-full sm:w-auto"
                      onClick={() => handleDeleteBlog(blog._id)}
                    >
                      <TrashIcon className="w-5 h-5" /> {/* Delete Icon */}
                      <span>Delete</span>
                    </button>
                    {/* Shareable Link */}
                    <button
                      className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 transition ease-in-out duration-300 flex items-center space-x-2 w-full sm:w-auto"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/genblog/${blog._id}`
                        );
                        alert("Link copied to clipboard!");
                      }}
                    >
                      <LinkIcon className="w-5 h-5" /> {/* Clipboard Icon */}
                      <span>Link</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Modal for Viewing Blog */}
      {viewingBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-40">
          <div className="relative bg-white p-8 rounded-lg shadow-2xl w-full max-w-3xl max-h-[75vh] overflow-y-auto">
            {/* Close Button */}
            <button
              className="absolute top-4 right-3 p-1 animate-bounce rounded-md bg-red-500 hover:bg-red-600 text-gray-500 hover:text-gray-800 focus:outline-none"
              onClick={() => setViewingBlog(null)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Blog Heading */}
            <h2 className="text-3xl font-extrabold mb-6 text-gray-900 text-center border-b pb-4">
              {viewingBlog.heading}
            </h2>
            <div className="flex flex-wrap justify-center gap-2 mb-6 border-b pb-4">
              {viewingBlog.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-200 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col items-center space-y-2">
              {/* Created Date */}
              <p className="text-sm font-mono text-green-600 font-medium">
                <span className="font-bold text-green-700">Created: </span>
                {new Date(viewingBlog.createdAt).toLocaleString()}
              </p>

              {/* Username */}
              <p className="text-sm font-mono text-blue-600 font-medium">
                <span className="font-bold font-mono text-blue-700">
                  Posted by:
                </span>
                {viewingBlog.username}{" "}
              </p>

              {/* Likes */}
              <p className="text-sm font-medium flex items-center space-x-2">
                <span className="text-red-500">❤️</span>
                <span className="text-red-500 font-bold">
                  {viewingBlog.Total_reaction}{" "}
                </span>
              </p>
            </div>

            {/* Blog Content */}
            <div className="prose max-w-none text-gray-900 leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {viewingBlog.content}
              </ReactMarkdown>
            </div>
            {/* Comment Box */}
            {/* Display Comments */}
            <div className="mt-4">
              <h4 className="text-lg font-semibold">Comments:</h4>
              <ul>
                {(comments[viewingBlog._id] || []).map((comment, index) => (
                  <li key={index} className="mt-2 p-2 border-b border-gray-300">
                    <p className="font-medium">{comment.username}</p>
                    <p className="text-sm">{comment.text}</p>
                  </li>
                ))}
              </ul>

              {/* Comment Input */}
              <form onSubmit={handleCommentfetch} className="mt-4">
                <textarea
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full p-2 border rounded-md"
                />
                <button
                  type="submit"
                  className="mt-2 px-4 py-2 bg-black text-white rounded-md"
                >
                  Post Comment
                </button>
              </form>
            </div>

            {/* Close Button at Bottom */}
            <div className="flex justify-center mt-8">
              <button
                className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition ease-in-out duration-300"
                onClick={() => setViewingBlog(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BloggingPage;
